import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import AuthLayout from './components/AuthLayout';
import Navigation from './components/Navigation';
import Dashboard from './pages/Dashboard';
import BarcodeScanner from './pages/BarcodeScanner';
import ScannedBarcodes from './pages/ScannedBarcodes';
import StockTrack from './pages/StockTrack';
import BarcodeGenerator from './pages/BarcodeGenerator';
import ImageProcessing from './pages/ImageProcessing';
import RobotControl from './pages/RobotControl';
import RackStatus from './pages/RackStatus';
import RackManagement from './pages/RackManagement';
import ProductManagement from './pages/ProductManagement';
import DeviceConnected from './pages/DeviceConnected';
import SavedScans from './pages/SavedScans';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import DevicesPage from './pages/DevicesPage';
import './styles/design-system.css';
import './App.css';

// Protected Route Component with Role-based Access Control
function ProtectedRoute({ children, requiredPath }) {
  const { isAuthenticated, hasPageAccess, isLoading } = useAuth();

  // Optimistic rendering: If authenticated (optimistically), render children even if loading.
  if (isLoading && !isAuthenticated()) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  if (requiredPath && !hasPageAccess(requiredPath)) {
    return (
      <div className="access-denied">
        <div className="access-denied-content">
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
          <p>Your role doesn't include access to this feature.</p>
          <button onClick={() => window.history.back()}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return children;
}

// Main App Content Component
function AppContent() {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading spinner only if we don't have a user session yet
  // If we have a user (optimistic load), we render the app immediately while verifying in background
  if (isLoading && !isAuthenticated()) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading RobBridge...</p>
        </div>
      </div>
    );
  }

  // Show login/signup page if not authenticated
  if (!isAuthenticated()) {
    return (
      <Routes>
        <Route element={<AuthLayout />}>
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="*" element={<LoginPage />} />
        </Route>
      </Routes>
    );
  }

  // Show main application if authenticated
  return (
    <div className="App">
      <Navigation />
      <main className="app-main-content">
        <Routes>
          <Route path="/" element={
            <ProtectedRoute requiredPath="/">
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/scanner" element={
            <ProtectedRoute requiredPath="/scanner">
              <BarcodeScanner />
            </ProtectedRoute>
          } />
          <Route path="/scanned-barcodes" element={
            <ProtectedRoute requiredPath="/scanned-barcodes">
              <ScannedBarcodes />
            </ProtectedRoute>
          } />
          <Route path="/stock-track" element={
            <ProtectedRoute requiredPath="/stock-track">
              <StockTrack />
            </ProtectedRoute>
          } />
          <Route path="/generator" element={
            <ProtectedRoute requiredPath="/generator">
              <BarcodeGenerator />
            </ProtectedRoute>
          } />
          <Route path="/image-processing" element={
            <ProtectedRoute requiredPath="/image-processing">
              <ImageProcessing />
            </ProtectedRoute>
          } />
          <Route path="/robot-control" element={
            <ProtectedRoute requiredPath="/robot-control">
              <RobotControl />
            </ProtectedRoute>
          } />
          <Route path="/rack-status" element={
            <ProtectedRoute requiredPath="/rack-status">
              <RackStatus />
            </ProtectedRoute>
          } />
          <Route path="/rack-management" element={
            <ProtectedRoute requiredPath="/rack-management">
              <RackManagement />
            </ProtectedRoute>
          } />
          <Route path="/product-management" element={
            <ProtectedRoute requiredPath="/product-management">
              <ProductManagement />
            </ProtectedRoute>
          } />
          <Route path="/device-connected" element={
            <ProtectedRoute requiredPath="/device-connected">
              <DeviceConnected />
            </ProtectedRoute>
          } />
          <Route path="/saved-scans" element={
            <ProtectedRoute requiredPath="/saved-scans">
              <SavedScans />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute requiredPath="/profile">
              <Profile />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute requiredPath="/settings">
              <Settings />
            </ProtectedRoute>
          } />
          <Route path="/devices" element={
            <ProtectedRoute requiredPath="/devices">
              <DevicesPage />
            </ProtectedRoute>
          } />
          <Route path="/login" element={<LoginPage />} />
        </Routes>
      </main>
    </div>
  );
}

// Main App Component with Auth Provider
// In development, handle both root and /bvs paths (for testing)
// In production, strictly use /bvs as per deployment requirement
const routerBasename = process.env.NODE_ENV === 'production' ? '/bvs' : '/';

function App() {
  return (
    <AuthProvider>
      <WebSocketProvider>
        <Router basename={routerBasename}>
          <AppContent />
        </Router>
      </WebSocketProvider>
    </AuthProvider>
  );
}

export default App;
